import { Button, Icon } from '@/components/atoms';
import type { IconName } from '@/components/atoms';
import type { ServiceLocationKind, ServicePreset } from '@/types/domain';
import { formatPrice } from '@/utils/price';
import styles from './ProfileFeed.module.css';

// F-170 B3.2: per-preset location chip dictionary. Lucide icons (R2 — no
// emoji as iconography). 'onsite' fallback covers presets that predate
// B0.4 (where the field is absent in the payload).
const LOCATION_LABEL: Record<
  ServiceLocationKind,
  { icon: IconName; label: string }
> = {
  online: { icon: 'globe', label: 'Онлайн' },
  delivery: { icon: 'truck', label: 'З доставкою' },
  onsite: { icon: 'map-pin', label: 'Виїзд' },
};

export interface ServiceCardProps {
  preset: ServicePreset;
  /** When true, render the 'Записатись' button that opens the slot picker. */
  bookable?: boolean;
  onAsk: () => void;
  onOrder: () => void;
  onBook?: () => void;
}

export function ServiceCard({
  preset,
  bookable,
  onAsk,
  onOrder,
  onBook,
}: ServiceCardProps): JSX.Element {
  const loc = LOCATION_LABEL[preset.location_kind ?? 'onsite'];
  return (
    <article className={styles.card}>
      <div className={styles.serviceHead}>
        <div className={styles.serviceTitle}>{preset.title}</div>
        {/* F-170 B3.3: route through formatPrice so "від X €" lives in
            one place for all card variants. */}
        <div className={styles.servicePrice}>
          {formatPrice(preset.price_eur, preset.price_is_from)}
        </div>
      </div>
      {/* F-170 B3.2: location-kind chip — sits between the head and the
          price/duration meta so the eye can scan the "where" before the
          "when". */}
      <div className={styles.serviceLocationChip}>
        <Icon name={loc.icon} size={14} />
        <span>{loc.label}</span>
      </div>
      {preset.duration_minutes ? (
        <div className={styles.serviceMeta}>
          <Icon name="calendar" size={14} />
          <span>{preset.duration_minutes} хв</span>
        </div>
      ) : null}
      {preset.description ? (
        <p className={styles.serviceDescription}>{preset.description}</p>
      ) : null}
      <div className={styles.serviceActions}>
        <Button variant="ghost" onClick={onAsk}>
          Спитати
        </Button>
        {bookable && onBook ? (
          <Button onClick={onBook}>Записатись</Button>
        ) : (
          <Button onClick={onOrder}>Замовити</Button>
        )}
      </div>
    </article>
  );
}
